import {
  useCapacityMode,
  useCreateShift,
  useSetCapacityMode,
  useShifts,
  useTables,
  useUpdateShift,
  useZones,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import {
  useReservationConfig,
  useUpdateReservationConfig,
} from '@/features/reservation-setup/hooks/useReservationConfig';
import { CapacityModeCard } from '@/features/reservation-setup/components/CapacityModeCard';
import { ChipInput } from '@/components/ChipInput';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import type { ReservationShift } from '@restaurant/api-client';
import { InfoTip } from '@/components/InfoTip';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavLeft,
  NavRight,
  NavTitle,
  Link as F7Link,
  Page,
  Popup,
  Preloader,
  f7,
} from 'framework7-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT, useErrorT } from '@/i18n';
import { apiErrorText } from '@/api/errors';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

/** Reservation-setup translation slice - passed into non-hook helpers/components
 *  so they don't have to call `useT()` themselves (F7 render helpers can't). */
type RS = ReturnType<typeof useT>['reservationSetup'];

type ShiftMode = 'SIMPLE' | 'ADVANCED';

/** Mode-tile copy, resolved from the active locale. Built per-render inside the
 *  component (needs `rs`); the icons are locale-independent. */
function shiftModeInfo(rs: RS): Record<
  ShiftMode,
  {
    title: string;
    description: string;
    confirmMsg: string;
    details: string;
    icon: string;
    features: string[];
  }
> {
  return {
    SIMPLE: {
      title: rs.simpleMode,
      description: rs.simpleModeDesc,
      confirmMsg: rs.simpleModeConfirm,
      icon: 'bolt_fill',
      features: [rs.simpleFeature1, rs.simpleFeature2, rs.simpleFeature3],
      details: rs.simpleModeDetails,
    },
    ADVANCED: {
      title: rs.advancedMode,
      description: rs.advancedModeDesc,
      confirmMsg: rs.advancedModeConfirm,
      icon: 'gear_alt_fill',
      features: [rs.advancedFeature1, rs.advancedFeature2, rs.advancedFeature3],
      details: rs.advancedModeDetails,
    },
  };
}

// Week order in the timeline: Mon → Sun. Stored as JS day-of-week values
// (0 = Sun, 1 = Mon …); we reorder for display only.
const TIMELINE_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

// Distinct hues for shift bars - cycled by shift index. Picked to stay legible
// on both light and dark F7 themes and visually distinct when stacked.
const SHIFT_COLORS = ['#0A84FF', '#FF9F0A', '#30D158', '#BF5AF2', '#FF453A', '#5E5CE6', '#64D2FF'];

// Neutral hue for the always-on baseline (default) coverage in the preview, so
// it reads as the backdrop rather than competing with the vivid window colours.
const BASELINE_COLOR = '#8e8e93';

/** "HH:mm" → minutes since midnight; returns null on malformed input so the
 *  caller can silently drop a corrupt shift instead of crashing the page. */
function timeToMinutes(t: string | null | undefined): number | null {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(':').map(Number);
  if (h > 24 || m > 59) return null;
  return h * 60 + m;
}

function describeDays(days: number[], rs: RS): string {
  if (days.length === 7) return rs.everyDay;
  return [...days]
    .sort((a, b) => a - b)
    .map((d) => rs.dayNamesShort[d])
    .join(', ');
}

function describeRules(s: ReservationShift, rs: RS): string {
  if (s.rules.mode === 'SIMPLE') {
    const r = s.rules;
    const parts: string[] = [];
    if (r.maxReservationsPerSlot != null) parts.push(rs.resSlot(r.maxReservationsPerSlot));
    if (r.maxCoversPerSlot != null) parts.push(rs.coversSlot(r.maxCoversPerSlot));
    return parts.length ? parts.join(' · ') : rs.noLimitation;
  }
  const r = s.rules;
  const parts: string[] = [];
  if (r.pacingMaxCovers != null) parts.push(rs.coversPer15(r.pacingMaxCovers));
  if (r.pacingMaxParties != null) parts.push(rs.partiesPer15(r.pacingMaxParties));
  return parts.length ? parts.join(' · ') : rs.noLimitation;
}

// Inset-list-group section heading: a BlockTitle with an optional info "ⓘ"
// (opens a dialog) and/or a right-aligned action (e.g. a "Manage" link or "+").
// Keeps every section header consistent across the page.
function SectionTitle({
  title,
  info,
  action,
}: {
  title: string;
  info?: { title: string; text: string; ariaLabel: string };
  action?: ReactNode;
}) {
  return (
    // BlockTitle defaults to `white-space: nowrap` + ellipsis (single line). On a
    // narrow phone a long localized title (e.g. the Vietnamese "Đồng thời mặc định
    // (hoạt động cùng lúc)") then collides with the InfoTip. Override to let the
    // title wrap onto multiple lines and keep the tip pinned to the first line.
    <BlockTitle style={{ height: 'auto', whiteSpace: 'normal' }}>
      <span style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}>
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          {title}
        </span>
        {info && <InfoTip text={info.text} aria-label={info.ariaLabel} style={{ flexShrink: 0 }} />}
        {action}
      </span>
    </BlockTitle>
  );
}

function ShiftsPage() {
  const t = useT();
  const errorMessages = useErrorT();
  const rs = t.reservationSetup;
  const modeInfo = shiftModeInfo(rs);
  const settingsBackLink = useSettingsDetailBackLink();
  const capacityModeQ = useCapacityMode();
  const capacityView = capacityModeQ.data;
  const setCapacityMode = useSetCapacityMode();
  const shiftsQ = useShifts();
  const { data: shifts, isLoading } = shiftsQ;
  // Only fetched / read in ADVANCED mode for the "what you have to seat people on"
  // summary card. Cheap enough to always run; the cache makes a no-op on SIMPLE.
  const { data: zones } = useZones();
  const { data: tables } = useTables(true);

  /** Roll up active tables → zone → seat-size buckets. Only tables that
   *  `countsForCapacity` are included - decorative/non-bookable seating
   *  isn't part of capacity planning. */
  const zoneSummaries = useMemo(() => {
    if (!tables) return null;
    const byZone = new Map<string | null, { seats: number; count: number }[]>();
    const seenZone = new Map<string | null, Map<number, number>>();
    for (const t of tables) {
      if (!t.isActive || !t.countsForCapacity) continue;
      const zoneId: string | null = t.zoneId ?? null;
      let buckets = seenZone.get(zoneId);
      if (!buckets) {
        buckets = new Map();
        seenZone.set(zoneId, buckets);
      }
      buckets.set(t.seats, (buckets.get(t.seats) ?? 0) + 1);
    }
    for (const [zoneId, buckets] of seenZone) {
      byZone.set(
        zoneId,
        [...buckets.entries()]
          .sort((a, b) => a[0] - b[0])
          .map(([seats, count]) => ({ seats, count })),
      );
    }
    return [...byZone.entries()]
      .map(([zoneId, sizes]) => {
        const zone = zoneId ? zones?.find((z) => z.id === zoneId) : null;
        const tableCount = sizes.reduce((s, x) => s + x.count, 0);
        const seatCount = sizes.reduce((s, x) => s + x.count * x.seats, 0);
        return {
          zoneId,
          label: zone?.label ?? rs.unassignedZone,
          sortOrder: zone?.sortOrder ?? Number.POSITIVE_INFINITY,
          tableCount,
          seatCount,
          sizes,
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [tables, zones, rs.unassignedZone]);

  const totalTables = zoneSummaries?.reduce((s, z) => s + z.tableCount, 0) ?? 0;
  const totalSeats = zoneSummaries?.reduce((s, z) => s + z.seatCount, 0) ?? 0;
  const createShift = useCreateShift();
  // Synchronous re-entry guard for the create-shift popup: isPending only flips
  // on the next render, so rapid taps could fire createShift.mutate twice before
  // React re-renders and produce a duplicate (overlapping) shift. The ref blocks
  // the second call immediately.
  const creatingWindowRef = useRef(false);
  // Same synchronous guard for the BASELINE ("Default") shift created by the
  // navbar Save when none exists yet — double-tapping before isPending flips would
  // otherwise create two baseline shifts. Updates (baseline already exists) and
  // buffer/config saves are idempotent by id, so only the create path needs it.
  const creatingBaselineRef = useRef(false);
  const updateShift = useUpdateShift();
  const rconfigQ = useReservationConfig();
  const { data: rconfig } = rconfigQ;
  const updateRConfig = useUpdateReservationConfig();

  // The page is unusable without its capacity mode, shifts, and config - surface
  // a single retryable error state rather than rendering half-empty sections
  // when any of the three fails (the backend occasionally 500s on these).
  const loadFailed = capacityModeQ.isError || shiftsQ.isError || rconfigQ.isError;
  const loadErrorObj = capacityModeQ.error ?? shiftsQ.error ?? rconfigQ.error;
  const retryLoad = () => {
    capacityModeQ.refetch();
    shiftsQ.refetch();
    rconfigQ.refetch();
  };
  const retrying = capacityModeQ.isFetching || shiftsQ.isFetching || rconfigQ.isFetching;
  const [popupOpen, setPopupOpen] = useState(false);
  const [newShift, setNewShift] = useState({ label: '', startTime: '17:00', endTime: '22:00' });

  // Turnaround buffer lives on ReservationConfig (global) but only takes effect
  // in ADVANCED, where bookings are assigned to tables. Drafted locally, saved
  // via the navbar Save button, re-synced when the stored value changes.
  const [bufferDraft, setBufferDraft] = useState('');
  const bufferTime = rconfig?.bufferTime;
  useEffect(() => {
    setBufferDraft(bufferTime != null ? String(bufferTime) : '');
  }, [bufferTime]);

  const activeMode = capacityView?.shiftMode;

  // Live shifts of the active mode only.
  const live = (shifts ?? []).filter((s) => s.mode === activeMode);
  const baseline = live.find((s) => s.scope === 'BASELINE') ?? null;
  const windowShifts = live.filter((s) => s.scope === 'WINDOW');
  const baselineRules = baseline?.rules.mode === 'SIMPLE' ? baseline.rules : null;
  const baselineAdvRules = baseline?.rules.mode === 'ADVANCED' ? baseline.rules : null;
  // No baseline yet → seed the inputs from the backend's default baseline rules
  // (returned at runtime on the capacity-mode view - the SAME values the booking
  // engine resolves for an unconfigured restaurant, so there's no client/server
  // version skew). The form shows what's actually in effect; saving on blur
  // materialises the baseline. A real baseline (SIMPLE or ADVANCED) always wins.
  const hasNoBaseline = baseline === null;
  const defaultRules = capacityView?.defaultBaselineRules ?? null;
  const simpleSrc =
    baselineRules ?? (hasNoBaseline && defaultRules?.mode === 'SIMPLE' ? defaultRules : null);
  const advSrc =
    baselineAdvRules ?? (hasNoBaseline && defaultRules?.mode === 'ADVANCED' ? defaultRules : null);

  // Inline baseline cap inputs - drafted locally, saved on blur. Holds both
  // SIMPLE and ADVANCED fields; only the pair matching `activeMode` is rendered
  // and submitted. Reset every time the underlying baseline rule values change.
  const [baselineDraft, setBaselineDraft] = useState({
    maxRes: '',
    maxCovers: '',
    pacingCovers: '',
    pacingParties: '',
    concurrentParties: '',
    concurrentCovers: '',
  });
  // Builds the draft from the stored baseline rules - used both to seed the
  // inputs and to revert them when a typed value is rejected (an empty string
  // renders the "no limitation" placeholder, i.e. unlimited).
  const computeBaselineDraft = useCallback(
    () => ({
      maxRes:
        simpleSrc?.maxReservationsPerSlot != null ? String(simpleSrc.maxReservationsPerSlot) : '',
      maxCovers: simpleSrc?.maxCoversPerSlot != null ? String(simpleSrc.maxCoversPerSlot) : '',
      pacingCovers: advSrc?.pacingMaxCovers != null ? String(advSrc.pacingMaxCovers) : '',
      pacingParties: advSrc?.pacingMaxParties != null ? String(advSrc.pacingMaxParties) : '',
      concurrentParties:
        advSrc?.maxConcurrentParties != null ? String(advSrc.maxConcurrentParties) : '',
      concurrentCovers:
        advSrc?.maxConcurrentCovers != null ? String(advSrc.maxConcurrentCovers) : '',
    }),
    // Depend on the primitive rule values, not the derived rule objects (rebuilt
    // every render), so the callback stays stable across renders.
    [
      simpleSrc?.maxReservationsPerSlot,
      simpleSrc?.maxCoversPerSlot,
      advSrc?.pacingMaxCovers,
      advSrc?.pacingMaxParties,
      advSrc?.maxConcurrentParties,
      advSrc?.maxConcurrentCovers,
    ],
  );
  useEffect(() => {
    setBaselineDraft(computeBaselineDraft());
  }, [computeBaselineDraft]);

  // Live baseline coverage for the preview - derived from the DRAFT inputs (not
  // the saved rules) so the preview reflects the numbers as they're typed,
  // before the blur-save round-trips to the server. Null when nothing is capped,
  // which also gates whether the baseline summary shows at all.
  const baselinePreview = useMemo((): { capText: string; capTooltip: string } | null => {
    const toCap = (v: string): number | null => {
      const t = v.trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isInteger(n) && n > 0 ? n : null;
    };
    if (activeMode === 'SIMPLE') {
      const res = toCap(baselineDraft.maxRes);
      const cov = toCap(baselineDraft.maxCovers);
      if (res == null && cov == null) return null;
      const r = res != null ? `${res}` : '∞';
      const c = cov != null ? `${cov}` : '∞';
      return { capText: `${r}/${c}`, capTooltip: rs.capResCoversPerSlot(r, c) };
    }
    if (activeMode === 'ADVANCED') {
      // Both caps: pacing (per-15-min) and concurrent (simultaneous). They mean
      // different things, so show both — "∞" for whichever axis is uncapped.
      const pacing = toCap(baselineDraft.pacingCovers);
      const concurrent = toCap(baselineDraft.concurrentCovers);
      if (pacing == null && concurrent == null) return null;
      const p = pacing != null ? `${pacing}` : '∞';
      const c = concurrent != null ? `${concurrent}` : '∞';
      return { capText: rs.advCap(p, c), capTooltip: rs.advCap(p, c) };
    }
    return null;
  }, [activeMode, baselineDraft, rs]);

  const switchMode = (target: ShiftMode) => {
    if (target === activeMode) return;
    const info = modeInfo[target];
    f7.dialog.confirm(info.confirmMsg, info.title, () => {
      setCapacityMode.mutate(
        { shiftMode: target },
        {
          onSuccess: () =>
            f7.toast.show({ text: rs.capacityModeColon(info.title), closeTimeout: 2000 }),
          onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failedSwitchMode),
        },
      );
    });
  };

  /** Parse "" → null, "12" → 12, anything else → NaN. */
  const parseCap = (v: string): number | null | typeof NaN => {
    const t = v.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isInteger(n) && n > 0 ? n : NaN;
  };

  // Build the baseline rules from the current draft, or null if any cap is
  // invalid (the caller shows the dialog + reverts the inputs).
  const buildBaselineRules = () => {
    const draft = baselineDraft;
    if (activeMode === 'SIMPLE') {
      const maxRes = parseCap(draft.maxRes);
      const maxCovers = parseCap(draft.maxCovers);
      if (Number.isNaN(maxRes) || Number.isNaN(maxCovers)) return null;
      return {
        mode: 'SIMPLE' as const,
        slotMinutes: 60,
        maxReservationsPerSlot: maxRes as number | null,
        maxCoversPerSlot: maxCovers as number | null,
      };
    }
    const pCovers = parseCap(draft.pacingCovers);
    const pParties = parseCap(draft.pacingParties);
    const cParties = parseCap(draft.concurrentParties);
    const cCovers = parseCap(draft.concurrentCovers);
    if (
      Number.isNaN(pCovers) ||
      Number.isNaN(pParties) ||
      Number.isNaN(cParties) ||
      Number.isNaN(cCovers)
    ) {
      return null;
    }
    return {
      mode: 'ADVANCED' as const,
      pacingMaxCovers: pCovers as number | null,
      pacingMaxParties: pParties as number | null,
      maxConcurrentParties: cParties as number | null,
      maxConcurrentCovers: cCovers as number | null,
      // Preserve any existing per-party-size buckets - no UI for them yet.
      partySizePacing: baselineAdvRules?.partySizePacing ?? [],
    };
  };

  // Has the operator edited any capacity field away from what's stored? Drives
  // the navbar Save button (lit only when dirty) + the unsaved-changes guard.
  const baselineDirty = (() => {
    const stored = computeBaselineDraft();
    return (Object.keys(stored) as (keyof typeof stored)[]).some(
      (k) => baselineDraft[k] !== stored[k],
    );
  })();
  const bufferDirty =
    activeMode === 'ADVANCED' && !!rconfig && bufferDraft.trim() !== String(rconfig.bufferTime);
  const capacityDirty = baselineDirty || bufferDirty;
  const savingCapacity = updateShift.isPending || createShift.isPending || updateRConfig.isPending;

  // Save every capacity field at once (navbar Save). Validation runs first: an
  // invalid entry shows a dialog and reverts that field instead of persisting.
  const saveCapacity = () => {
    if (!activeMode) return;
    // Block re-entry while the baseline create is in flight (spam-tap → duplicate
    // baseline shifts). isPending lags a render, so guard with the synchronous ref.
    if (creatingBaselineRef.current) return;

    // Turnaround buffer (ADVANCED only).
    let bufferPatch: { bufferTime: number } | null = null;
    if (activeMode === 'ADVANCED' && rconfig) {
      const raw = bufferDraft.trim();
      const n = raw === '' ? 0 : Number(raw);
      if (!Number.isInteger(n) || n < 0) {
        setBufferDraft(String(rconfig.bufferTime));
        f7.dialog.alert(rs.bufferMustBePositive);
        return;
      }
      if (n !== rconfig.bufferTime) bufferPatch = { bufferTime: n };
    }

    // Capacity caps.
    const rules = buildBaselineRules();
    if (!rules) {
      setBaselineDraft(computeBaselineDraft());
      f7.dialog.alert(rs.capsPositiveWhole);
      return;
    }

    const notifySaved = () => f7.toast.show({ text: t.saved, closeTimeout: 2000 });

    if (bufferPatch && rconfig) {
      updateRConfig.mutate(bufferPatch, {
        // When caps also change, let the baseline save own the "Saved" toast so
        // it only shows once.
        onSuccess: baselineDirty ? undefined : notifySaved,
        onError: (e) => {
          f7.toast.show({
            text: e instanceof Error ? e.message : rs.saveFailed,
            closeTimeout: 3000,
          });
          setBufferDraft(String(rconfig.bufferTime));
        },
      });
    }

    if (baselineDirty) {
      const onError = (e: unknown) =>
        f7.dialog.alert(apiErrorText(e, errorMessages, rs.failedSaveShift));
      if (baseline) {
        updateShift.mutate(
          { shiftId: baseline.id, body: { rules } },
          { onSuccess: notifySaved, onError },
        );
      } else {
        creatingBaselineRef.current = true;
        createShift.mutate(
          { scope: 'BASELINE', label: 'Default', defaultDurationMinutes: 60, rules },
          {
            onSuccess: () => {
              creatingBaselineRef.current = false;
              notifySaved();
            },
            onError: (e: unknown) => {
              creatingBaselineRef.current = false;
              onError(e);
            },
          },
        );
      }
    }
  };

  // ── New time-specific shift popup ──
  const openCreateWindow = () => {
    setNewShift({ label: '', startTime: '17:00', endTime: '22:00' });
    setPopupOpen(true);
  };
  const closePopup = () => setPopupOpen(false);
  const submitWindow = () => {
    // Block re-entry while a create is already in flight (spam-tap → duplicates).
    if (creatingWindowRef.current || createShift.isPending) return;
    const label = newShift.label.trim();
    if (!label) return f7.dialog.alert(rs.nameRequired);
    if (!/^\d{2}:\d{2}$/.test(newShift.startTime) || !/^\d{2}:\d{2}$/.test(newShift.endTime)) {
      return f7.dialog.alert(rs.timesMustBeHHmm);
    }
    if (newShift.startTime >= newShift.endTime) return f7.dialog.alert(rs.startBeforeEnd);
    creatingWindowRef.current = true;

    // Rules must match the active capacity mode - creating a SIMPLE shift while
    // in ADVANCED mode (or vice versa) would land it in the wrong "live" set
    // and fail validation against the mode-specific contract.
    const advancedBaseline = baseline?.rules.mode === 'ADVANCED' ? baseline.rules : null;
    const rules =
      activeMode === 'ADVANCED'
        ? {
            mode: 'ADVANCED' as const,
            pacingMaxCovers: advancedBaseline?.pacingMaxCovers ?? null,
            pacingMaxParties: advancedBaseline?.pacingMaxParties ?? null,
            maxConcurrentParties: advancedBaseline?.maxConcurrentParties ?? null,
            maxConcurrentCovers: advancedBaseline?.maxConcurrentCovers ?? null,
            partySizePacing: advancedBaseline?.partySizePacing ?? [],
          }
        : {
            // Prefill caps from the baseline (if any) - user can tighten them
            // on the detail page after creation.
            mode: 'SIMPLE' as const,
            slotMinutes: 60,
            maxReservationsPerSlot: baselineRules?.maxReservationsPerSlot ?? null,
            maxCoversPerSlot: baselineRules?.maxCoversPerSlot ?? null,
          };
    createShift.mutate(
      {
        scope: 'WINDOW',
        label,
        startTime: newShift.startTime,
        endTime: newShift.endTime,
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        defaultDurationMinutes: 60,
        rules,
      },
      {
        onSuccess: () => {
          creatingWindowRef.current = false;
          closePopup();
          // Stay on the capacity overview (this page) after creating a shift; the
          // new shift shows in the list and the user can tap it to fine-tune
          // days/caps. Previously this pushed straight to the shift detail page.
        },
        onError: () => {
          // Reset the re-entry guard so the user can retry after fixing the
          // clash. The global mutationCache handler (query-client.ts) already
          // surfaces the localized error dialog — alerting here too made the
          // same overlap message pop up twice.
          creatingWindowRef.current = false;
        },
      },
    );
  };

  if (loadFailed) {
    return (
      <Page name="capacity">
        <Navbar title={rs.capacity} backLink={settingsBackLink} />
        <div
          className="setting-card"
          style={{ textAlign: 'center', paddingTop: 28, paddingBottom: 28 }}
        >
          <span
            className="setting-card__icon"
            style={{
              margin: '0 auto 14px',
              width: 48,
              height: 48,
              background: 'rgba(255,69,58,0.12)',
            }}
          >
            <i className="f7-icons" style={{ fontSize: 26, color: '#FF453A' }}>
              exclamationmark_triangle
            </i>
          </span>
          <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>
            {rs.couldntLoadCapacity}
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'var(--f7-list-item-subtitle-text-color)',
              marginBottom: 18,
              lineHeight: 1.45,
            }}
          >
            {loadErrorObj instanceof Error ? loadErrorObj.message : rs.serverNoResponse}
          </div>
          <Button
            fill
            round
            onClick={retryLoad}
            disabled={retrying}
            style={{ display: 'inline-block' }}
          >
            {retrying ? rs.retrying : rs.tryAgain}
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <Page name="capacity">
      <Navbar title={rs.capacity} backLink={settingsBackLink}>
        <SettingsSaveNav isDirty={capacityDirty} saving={savingCapacity} onSave={saveCapacity} />
      </Navbar>

      <CapacityModeCard
        options={(['SIMPLE', 'ADVANCED'] as ShiftMode[]).map((m) => ({
          value: m,
          icon: modeInfo[m].icon,
          title: modeInfo[m].title,
          features: modeInfo[m].features,
          details: modeInfo[m].details,
        }))}
        value={activeMode}
        onChange={switchMode}
        disabled={setCapacityMode.isPending || !activeMode}
        heading={rs.capacityMode}
        note={rs.capacityModeFooter}
        infoAriaLabel={rs.aboutSection}
      />

      {/* Default capacity - its own section, edited inline. Maps 1-1 to the
          BASELINE shift (auto-upserted on blur). */}
      {activeMode === 'ADVANCED' && (
        <>
          <SectionTitle
            title={rs.tablesAvailable}
            action={<F7Link href="/settings/reservation/tables">{rs.manage}</F7Link>}
          />
          {zoneSummaries && zoneSummaries.length > 0 && (
            <BlockFooter style={{ marginTop: -6 }}>
              {rs.bookableTablesInfo(
                totalTables,
                totalSeats,
                zoneSummaries.length,
                zoneSummaries.length === 1 ? '' : 's',
              )}
            </BlockFooter>
          )}
          {zoneSummaries == null ? (
            <Block strongIos strongMd insetIos insetMd className="text-align-center">
              <Preloader />
            </Block>
          ) : zoneSummaries.length === 0 ? (
            <Block strongIos strongMd insetIos insetMd>
              <div style={{ fontSize: 14, color: 'var(--f7-list-item-subtitle-text-color)' }}>
                {rs.noBookableTablesPrefix}{' '}
                <F7Link href="/settings/reservation/tables">{rs.addTables}</F7Link>{' '}
                {rs.noBookableTablesSuffix}
              </div>
            </Block>
          ) : (
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
              {zoneSummaries.map((z) => (
                <ListItem key={z.zoneId ?? '__none'} noChevron>
                  <span slot="title">{z.label}</span>
                  <span
                    slot="subtitle"
                    style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', display: 'block' }}
                  >
                    {z.sizes.map((s) => rs.topGroup(s.count, s.seats)).join(' · ')}
                  </span>
                  <span
                    slot="after"
                    style={{ color: 'var(--f7-list-item-subtitle-text-color)', fontSize: 14 }}
                  >
                    {rs.tblShortSeats(z.tableCount, z.seatCount)}
                  </span>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}

      {activeMode === 'ADVANCED' ? (
        <>
          <SectionTitle
            title={rs.tableturnaround}
            info={{
              title: rs.tableturnaround,
              text: rs.tableturnaroundInfo,
              ariaLabel: rs.aboutSection(rs.tableturnaround),
            }}
          />
          <BlockFooter style={{ marginTop: -6 }}>{rs.bufferInfo}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.bufferMin}
              type="number"
              placeholder="0"
              value={bufferDraft}
              onInput={(e) => setBufferDraft((e.target as HTMLInputElement).value)}
            />
          </List>

          <SectionTitle
            title={rs.defaultPacing}
            info={{
              title: rs.pacingTitle,
              text: rs.pacingInfo,
              ariaLabel: rs.aboutSection(rs.pacingTitle),
            }}
          />
          <BlockFooter style={{ marginTop: -6 }}>
            <span style={{ color: '#ff3b30', fontWeight: 600 }}>⚠ {rs.shiftOverrideWarning}</span>
          </BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.maxCovers}
              type="number"
              placeholder={rs.noLimitation}
              value={baselineDraft.pacingCovers}
              onInput={(e) =>
                setBaselineDraft((d) => ({
                  ...d,
                  pacingCovers: (e.target as HTMLInputElement).value,
                }))
              }
            />
            <ListInput
              label={rs.maxParties}
              type="number"
              placeholder={rs.noLimitation}
              value={baselineDraft.pacingParties}
              onInput={(e) =>
                setBaselineDraft((d) => ({
                  ...d,
                  pacingParties: (e.target as HTMLInputElement).value,
                }))
              }
            />
          </List>
          <BlockFooter>{rs.leaveBlankNoLimitation}</BlockFooter>

          <SectionTitle
            title={rs.defaultConcurrent}
            info={{
              title: rs.concurrentTitle,
              text: rs.concurrentInfo,
              ariaLabel: rs.aboutSection(rs.concurrentTitle),
            }}
          />
          <BlockFooter style={{ marginTop: -6 }}>{rs.concurrentFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.maxParties}
              type="number"
              placeholder={rs.noLimitation}
              value={baselineDraft.concurrentParties}
              onInput={(e) =>
                setBaselineDraft((d) => ({
                  ...d,
                  concurrentParties: (e.target as HTMLInputElement).value,
                }))
              }
            />
            <ListInput
              label={rs.maxCovers}
              type="number"
              placeholder={rs.noLimitation}
              value={baselineDraft.concurrentCovers}
              onInput={(e) =>
                setBaselineDraft((d) => ({
                  ...d,
                  concurrentCovers: (e.target as HTMLInputElement).value,
                }))
              }
            />
          </List>
        </>
      ) : (
        <>
          <BlockTitle>{rs.capacityAndLimit}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{rs.leaveBlankNoLimitation}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.maxReservationsSlot}
              type="number"
              placeholder={rs.noLimitation}
              value={baselineDraft.maxRes}
              onInput={(e) =>
                setBaselineDraft((d) => ({ ...d, maxRes: (e.target as HTMLInputElement).value }))
              }
            />
            <ListInput
              label={rs.maxCoversSlot}
              type="number"
              placeholder={rs.noLimitation}
              value={baselineDraft.maxCovers}
              onInput={(e) =>
                setBaselineDraft((d) => ({
                  ...d,
                  maxCovers: (e.target as HTMLInputElement).value,
                }))
              }
            />
          </List>
        </>
      )}

      <SectionTitle
        title={rs.timeSpecificShifts}
        action={<F7Link iconF7="plus" onClick={openCreateWindow} />}
      />
      {isLoading ? (
        <Block strongIos strongMd insetIos insetMd className="text-align-center">
          <Preloader />
        </Block>
      ) : windowShifts.length === 0 ? (
        <Block strongIos strongMd insetIos insetMd className="text-align-center">
          <div style={{ fontSize: 14, color: 'var(--f7-list-item-subtitle-text-color)' }}>
            {rs.noTimeSpecificShifts}
          </div>
          <p style={{ marginTop: 16 }}>
            <Button fill round onClick={openCreateWindow}>
              {rs.addAShift}
            </Button>
          </p>
        </Block>
      ) : (
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
          {windowShifts.map((s) =>
            s.scope === 'WINDOW' ? (
              <ListItem
                key={s.id}
                link={`/settings/reservation/shifts/${s.id}`}
                animate
                title={s.label}
                subtitle={rs.windowSubtitle(s.startTime, s.endTime, describeDays(s.daysOfWeek, rs))}
                text={describeRules(s, rs)}
              >
                <i slot="media" className="f7-icons">
                  clock
                </i>
              </ListItem>
            ) : null,
          )}
        </List>
      )}

      {/* Visual coverage timeline - kept below the time-specific shift list so it
          reads as a summary of everything configured above. */}
      {(windowShifts.length > 0 || baselinePreview) && (
        <ShiftTimeline
          rs={rs}
          baseline={baselinePreview}
          shifts={windowShifts.filter(
            (s): s is Extract<ReservationShift, { scope: 'WINDOW' }> => s.scope === 'WINDOW',
          )}
        />
      )}

      <Popup opened={popupOpen} onPopupClosed={closePopup}>
        <Page>
          <Navbar>
            <NavLeft>
              <F7Link popupClose>{t.cancel}</F7Link>
            </NavLeft>
            <NavTitle>{rs.newTimeSpecificShift}</NavTitle>
            <NavRight>
              <F7Link
                onClick={submitWindow}
                className={createShift.isPending ? 'disabled' : undefined}
              >
                {t.saveShort}
              </F7Link>
            </NavRight>
          </Navbar>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.name}
              type="text"
              placeholder={rs.lunchDinner}
              value={newShift.label}
              onInput={(e) =>
                setNewShift((s) => ({ ...s, label: (e.target as HTMLInputElement).value }))
              }
            />
            <ListItem noChevron title={rs.startTime}>
              <ChipInput
                slot="after"
                type="time"
                value={newShift.startTime}
                onChange={(v) => setNewShift((s) => ({ ...s, startTime: v }))}
              />
            </ListItem>
            <ListItem noChevron title={rs.endTime}>
              <ChipInput
                slot="after"
                type="time"
                value={newShift.endTime}
                onChange={(v) => setNewShift((s) => ({ ...s, endTime: v }))}
              />
            </ListItem>
          </List>
          <BlockFooter>
            <span style={{ color: '#ff3b30', fontWeight: 600 }}>⚠ {rs.detailOverrideWarning}</span>
          </BlockFooter>
          <BlockFooter>{rs.shiftFooter}</BlockFooter>
        </Page>
      </Popup>
    </Page>
  );
}

export default ShiftsPage;

// ── Weekly coverage timeline ───────────────────────────────────────────────
// 7 rows (Mon → Sun), each a 24-hour strip. Every shift draws a coloured bar
// on its active days from startTime → endTime. Tooltip + a colour-key legend
// underneath make it readable when shifts overlap or share days.
//
// Two shifts that overlap in time on a shared day used to draw at the exact
// same x/width and the later one fully hid the earlier - so an operator with
// e.g. "17–22 Mon-Wed" + "17–22 Sun-Wed,Sat" saw only one bar. We now assign
// each shift to a horizontal LANE (stacked sub-strips within a day row) so
// overlapping shifts are both visible. Lanes are global (a shift keeps the same
// lane across the week) so the eye can track a shift row-to-row.
//
// Pure CSS / percentage math - no chart lib. The container is `position:
// relative` and each bar is `position: absolute` with `left/width` set from
// the shift's minute-of-day fraction and `top` from its lane.

type WindowShift = Extract<ReservationShift, { scope: 'WINDOW' }>;

const TIMELINE_HOURS = [0, 6, 12, 18, 24];

// Lane geometry for stacking overlapping shifts within a single day row.
const LANE_HEIGHT = 18; // px per lane
const LANE_GAP = 2; // px between stacked lanes

/** Two bars conflict (can't share a lane) when they're active on at least one
 *  common day AND their time ranges overlap. */
function barsConflict(a: Bar, b: Bar): boolean {
  let sharesDay = false;
  for (const d of a.days) {
    if (b.days.has(d)) {
      sharesDay = true;
      break;
    }
  }
  if (!sharesDay) return false;
  return a.leftPct < b.leftPct + b.widthPct && b.leftPct < a.leftPct + a.widthPct;
}

/** Greedy interval-graph colouring → a stable lane index per bar. Bars are
 *  processed left-to-right (then by id for determinism on a tie) and each takes
 *  the lowest lane not used by an already-placed conflicting bar. Non-conflicting
 *  shifts reuse lane 0, so the timeline only grows as tall as the worst overlap. */
function assignLanes(bars: Bar[]): { laneOf: Map<string, number>; laneCount: number } {
  const ordered = [...bars].sort((a, b) => a.leftPct - b.leftPct || a.id.localeCompare(b.id));
  const laneOf = new Map<string, number>();
  let laneCount = 0;
  for (const bar of ordered) {
    const taken = new Set<number>();
    for (const other of ordered) {
      if (other.id === bar.id) continue;
      const lane = laneOf.get(other.id);
      if (lane !== undefined && barsConflict(bar, other)) taken.add(lane);
    }
    let lane = 0;
    while (taken.has(lane)) lane++;
    laneOf.set(bar.id, lane);
    if (lane + 1 > laneCount) laneCount = lane + 1;
  }
  return { laneOf, laneCount: Math.max(1, laneCount) };
}

function ShiftTimeline({
  rs,
  shifts,
  baseline,
}: {
  rs: RS;
  shifts: WindowShift[];
  baseline: { capText: string; capTooltip: string } | null;
}) {
  // Sort by start time so the legend reads chronologically.
  const sorted = [...shifts].sort((a, b) => a.startTime.localeCompare(b.startTime));

  const bars = sorted
    .map((s, i) => {
      const start = timeToMinutes(s.startTime);
      const end = timeToMinutes(s.endTime);
      if (start == null || end == null || end <= start) return null;
      // Bar label. SIMPLE shows both per-slot caps as "reservations/covers"
      // (e.g. 4/16); ∞ for an uncapped side. ADVANCED shows pacing + concurrent
      // covers compactly ("20 · 50"), the full labelled form in the tooltip.
      // "∞" alone when nothing is capped.
      let capText = '∞';
      let capTooltip = rs.noLimitation;
      if (s.rules.mode === 'SIMPLE') {
        const res = s.rules.maxReservationsPerSlot;
        const cov = s.rules.maxCoversPerSlot;
        if (res != null || cov != null) {
          const r = res != null ? `${res}` : '∞';
          const c = cov != null ? `${cov}` : '∞';
          capText = `${r}/${c}`;
          capTooltip = rs.capResCoversPerSlot(r, c);
        }
      } else if (
        s.rules.mode === 'ADVANCED' &&
        (s.rules.pacingMaxCovers != null || s.rules.maxConcurrentCovers != null)
      ) {
        const p = s.rules.pacingMaxCovers != null ? `${s.rules.pacingMaxCovers}` : '∞';
        const c = s.rules.maxConcurrentCovers != null ? `${s.rules.maxConcurrentCovers}` : '∞';
        capText = rs.advCapCompact(p, c);
        capTooltip = rs.advCap(p, c);
      }
      return {
        id: s.id,
        label: s.label,
        startTime: s.startTime,
        endTime: s.endTime,
        days: new Set(s.daysOfWeek),
        color: SHIFT_COLORS[i % SHIFT_COLORS.length],
        leftPct: (start / (24 * 60)) * 100,
        widthPct: ((end - start) / (24 * 60)) * 100,
        capText,
        capTooltip,
      };
    })
    .filter((b): b is NonNullable<typeof b> => b !== null);

  // Stack overlapping shifts into horizontal lanes so none is hidden behind another.
  const { laneOf, laneCount } = assignLanes(bars);

  return (
    <>
      <BlockTitle>{rs.preview}</BlockTitle>
      <Block strongIos strongMd insetIos insetMd>
        {/* Baseline (default) coverage - driven by the live draft caps so the
            numbers update as the user types. Always-on, so it reads as a single
            summary row rather than a per-day bar. */}
        {baseline && (
          <div
            title={baseline.capTooltip}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              marginBottom: bars.length > 0 ? 12 : 0,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: BASELINE_COLOR,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1, color: 'var(--f7-list-item-subtitle-text-color)' }}>
              {rs.defaultCoverage}
            </span>
            <span style={{ fontWeight: 600 }}>{baseline.capText}</span>
          </div>
        )}

        {bars.length > 0 && (
          <>
            <div
              style={{ display: 'grid', gridTemplateColumns: '32px 1fr', rowGap: 6, columnGap: 8 }}
            >
              {/* Header: hour ticks across the top */}
              <div />
              <div style={{ position: 'relative', height: 14 }}>
                {TIMELINE_HOURS.map((h) => (
                  <span
                    key={h}
                    style={{
                      position: 'absolute',
                      left: `${(h / 24) * 100}%`,
                      transform:
                        h === 0
                          ? 'translateX(0)'
                          : h === 24
                            ? 'translateX(-100%)'
                            : 'translateX(-50%)',
                      fontSize: 10,
                      color: 'var(--f7-list-item-subtitle-text-color)',
                    }}
                  >
                    {h === 24 ? '24' : String(h).padStart(2, '0')}
                  </span>
                ))}
              </div>

              {/* One row per day. Mon → Sun. */}
              {TIMELINE_DAY_ORDER.map((dow) => (
                <Row
                  key={dow}
                  dow={dow}
                  bars={bars}
                  rs={rs}
                  laneOf={laneOf}
                  laneCount={laneCount}
                />
              ))}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
              {bars.map((b) => (
                <span
                  key={b.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: b.color,
                      display: 'inline-block',
                    }}
                  />
                  {b.label}
                  <span style={{ color: 'var(--f7-list-item-subtitle-text-color)' }}>
                    {b.startTime}–{b.endTime}
                  </span>
                </span>
              ))}
            </div>
          </>
        )}
      </Block>
    </>
  );
}

interface Bar {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
  days: Set<number>;
  color: string;
  leftPct: number;
  widthPct: number;
  capText: string;
  capTooltip: string;
}

function Row({
  dow,
  bars,
  rs,
  laneOf,
  laneCount,
}: {
  dow: number;
  bars: Bar[];
  rs: RS;
  laneOf: Map<string, number>;
  laneCount: number;
}) {
  // Row height grows with the number of stacked lanes so every overlapping
  // shift gets its own strip instead of being painted over.
  const rowHeight = laneCount * LANE_HEIGHT + (laneCount - 1) * LANE_GAP;
  return (
    <>
      <div
        style={{
          fontSize: 11,
          color: 'var(--f7-list-item-subtitle-text-color)',
          alignSelf: 'center',
        }}
      >
        {rs.dayNamesShort[dow]}
      </div>
      <div
        style={{
          position: 'relative',
          height: rowHeight,
          background: 'rgba(120,120,128,0.10)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Hour gridlines - 6 / 12 / 18 (skip 0 and 24 since they're the edges). */}
        {[6, 12, 18].map((h) => (
          <div
            key={h}
            style={{
              position: 'absolute',
              left: `${(h / 24) * 100}%`,
              top: 0,
              bottom: 0,
              width: 1,
              background: 'rgba(120,120,128,0.18)',
            }}
          />
        ))}
        {bars
          .filter((b) => b.days.has(dow))
          .map((b) => {
            const lane = laneOf.get(b.id) ?? 0;
            return (
              <div
                key={b.id}
                title={`${b.label} · ${b.startTime}–${b.endTime} · ${b.capTooltip}`}
                style={{
                  position: 'absolute',
                  left: `${b.leftPct}%`,
                  width: `${b.widthPct}%`,
                  top: lane * (LANE_HEIGHT + LANE_GAP),
                  height: LANE_HEIGHT,
                  background: b.color,
                  borderRadius: 2,
                  opacity: 0.9,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: `${LANE_HEIGHT}px`,
                  textAlign: 'center',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textShadow: '0 0 2px rgba(0,0,0,0.35)',
                }}
              >
                {/* Only render the label if the bar is wide enough to fit it.
                    The "res/covers" form needs more room than a single digit, so
                    gate longer labels on a wider bar; otherwise it clips to noise. */}
                {b.widthPct > (b.capText.length > 2 ? 5 : 2.5) ? b.capText : ''}
              </div>
            );
          })}
      </div>
    </>
  );
}
